import React, { useState } from 'react';
import {
    AppBar,
    Toolbar,
    Typography,
    Button,
    IconButton,
    Drawer,
    List,
    ListItem,
    ListItemText,
    Menu,
    useMediaQuery,
    Divider,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import Box from '@mui/material/Box';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import TwitterIcon from '@mui/icons-material/Twitter';
import GitHubIcon from '@mui/icons-material/GitHub';
import InstagramIcon from '@mui/icons-material/Instagram';
import FacebookIcon from '@mui/icons-material/Facebook';

const Header = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [menuAnchorEl, setMenuAnchorEl] = useState(null);

    const location = useLocation();
    const navigate = useNavigate();

    // Map paths to tab names for active state
    const isActive = (path) => {
        if (path === '/' && location.pathname === '/') return true;
        if (path !== '/' && location.pathname.startsWith(path)) return true;
        return false;
    };

    const [scrolled, setScrolled] = useState(false);

    React.useEffect(() => {
        const handleScroll = () => {
            setScrolled(window.scrollY > 50);
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const handleNavigation = (path) => {
        window.scrollTo(0, 0);
        navigate(path);
        setDrawerOpen(false);
    };

    return (
        <AppBar
            position="fixed"
            elevation={scrolled ? 4 : 0}
            sx={{
                backgroundColor: scrolled
                    ? 'rgba(255, 255, 255, 0.98)' // Solid white when scrolled
                    : 'rgba(255, 255, 255, 0.9)', // Slightly transparent white at top (still white)
                backdropFilter: 'blur(10px)',
                borderBottom: scrolled ? '1px solid rgba(0,0,0,0.05)' : 'none',
                transition: 'all 0.3s ease-in-out',
                boxShadow: scrolled ? '0 4px 20px rgba(0,0,0,0.05)' : 'none'
            }}
        >
            <Toolbar>
                {/* Logo */}
                <img
                    src="/logo.png"
                    alt="GrowAITech Logo"
                    style={{ height: '45px', width: 'auto', marginRight: '12px', cursor: 'pointer' }}
                    onClick={() => handleNavigation("/")}
                />

                <Typography
                    variant="h6"
                    sx={{
                        flexGrow: 1,
                        fontFamily: 'Lato',
                        fontSize: { xs: '20px', sm: '24px' }, // Smaller font on mobile
                        fontWeight: 'bold',
                        cursor: 'pointer',
                        letterSpacing: '-0.5px',
                        lineHeight: 1
                    }}
                    onClick={() => handleNavigation("/")}
                >
                    <span style={{ color: theme.palette.primary.main }}>G</span>
                    <span style={{ color: theme.palette.secondary.main }}>row</span>
                    <span style={{ color: theme.palette.primary.main }}>A</span>
                    <span style={{ color: theme.palette.secondary.main }}>I</span>
                    <span style={{ color: theme.palette.primary.main }}>T</span>
                    <span style={{ color: theme.palette.secondary.main }}>ech</span>
                </Typography>

                {/* Desktop Menu */}
                {!isMobile && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {[
                            { label: 'Home', path: '/' },
                            { label: 'About', path: '/about' },
                            { label: 'Services', path: '/services' },
                            { label: 'Careers', path: '/careers' },
                            { label: 'News & Media', path: '/news' },
                            { label: 'Contact', path: '/contact' },
                        ].map(({ label, path }) => (
                            <Button
                                key={label}
                                onClick={() => handleNavigation(path)}
                                sx={{
                                    color: isActive(path)
                                        ? theme.palette.primary.main
                                        : '#555',
                                    fontFamily: 'Lato',
                                    fontSize: '16px',
                                    fontWeight: isActive(path) ? '800' : '600',
                                    textTransform: 'none',
                                    mx: 1,
                                    position: 'relative',
                                    transition: 'all 0.3s',
                                    '&:hover': {
                                        color: theme.palette.primary.main,
                                        backgroundColor: 'rgba(26, 158, 127, 0.05)'
                                    },
                                    '&:after': isActive(path) ? {
                                        content: '""',
                                        position: 'absolute',
                                        bottom: 0,
                                        left: '50%',
                                        transform: 'translateX(-50%)',
                                        width: '6px',
                                        height: '6px',
                                        borderRadius: '50%',
                                        backgroundColor: theme.palette.primary.main
                                    } : {}
                                }}
                            >
                                {label}
                            </Button>
                        ))}

                        {/* Social Menu */}
                        <IconButton
                            onClick={(event) => setMenuAnchorEl(event.currentTarget)}
                            sx={{ color: theme.palette.primary.main, ml: 1 }}
                        >
                            <MenuIcon />
                        </IconButton>
                        <Menu
                            anchorEl={menuAnchorEl}
                            open={Boolean(menuAnchorEl)}
                            onClose={() => setMenuAnchorEl(null)}
                            sx={{ mt: 1, '& .MuiPaper-root': { width: '180px', padding: '5px' } }}
                        >
                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px' }}>
                                {socialLinks.map(({ href, icon, label }) => (
                                    <IconButton key={label} href={href} target="_blank" sx={socialButtonStyles}>
                                        {icon}
                                        <Typography variant="body2" sx={{ marginLeft: 2, fontWeight: 'bold' }}>
                                            {label}
                                        </Typography>
                                    </IconButton>
                                ))}
                            </div>
                        </Menu>
                    </Box>
                )}

                {/* Mobile Drawer */}
                {isMobile && (
                    <>
                        <IconButton
                            onClick={() => setDrawerOpen(true)}
                            sx={{ color: theme.palette.primary.main }}
                        >
                            <MenuIcon />
                        </IconButton>
                        <Drawer
                            anchor="right"
                            open={drawerOpen}
                            onClose={() => setDrawerOpen(false)}
                            sx={{ zIndex: 1250 }} // Ensure it's above the Header (1100)
                            PaperProps={{
                                sx: {
                                    width: 'auto',
                                    minWidth: '200px',
                                    background: 'linear-gradient(135deg, #002613 0%, #034522 100%)',
                                    backdropFilter: 'blur(10px)',
                                    color: 'white',
                                    borderLeft: '1px solid rgba(255,255,255,0.1)'
                                }
                            }}
                        >
                            <Box
                                role="presentation"
                                component={motion.div}
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                transition={{ duration: 0.5 }}
                                sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}
                            >
                                <Box sx={{ p: 2, display: 'flex', justifyContent: 'flex-end' }}>
                                    <IconButton onClick={() => setDrawerOpen(false)} sx={{ color: 'white', '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' } }}>
                                        <CloseIcon />
                                    </IconButton>
                                </Box>

                                <List sx={{ px: 2, overflowY: 'auto', overflowX: 'hidden' }}>
                                    {[
                                        { label: 'Home', path: '/' },
                                        { label: 'About', path: '/about' },
                                        { label: 'Services', path: '/services' },
                                        { label: 'Careers', path: '/careers' },
                                        { label: 'News & Media', path: '/news' },
                                        { label: 'Contact', path: '/contact' },
                                        { label: 'Privacy', path: '/privacy-policy' },
                                        { label: 'Terms', path: '/terms-of-service' }
                                    ].map(({ label, path }, index) => (
                                        <motion.div
                                            key={label}
                                            initial={{ opacity: 0, x: 50 }}
                                            animate={{ opacity: 1, x: 0 }}
                                            transition={{ delay: index * 0.05, type: "spring", stiffness: 100 }}
                                            whileHover={{ scale: 1.02, x: 5 }}
                                            whileTap={{ scale: 0.95 }}
                                        >
                                            <ListItem
                                                button
                                                onClick={() => handleNavigation(path)}
                                                dense
                                                sx={{
                                                    borderRadius: 1,
                                                    mb: 0.5,
                                                    py: 0.5,
                                                    bgcolor: isActive(path) ? 'rgba(26, 158, 127, 0.2)' : 'transparent',
                                                    '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' }
                                                }}
                                            >
                                                <ListItemText
                                                    primary={label}
                                                    primaryTypographyProps={{
                                                        fontWeight: isActive(path) ? '800' : '500',
                                                        color: isActive(path) ? '#1A9E7F' : 'white',
                                                        fontSize: '0.9rem',
                                                        letterSpacing: '0.5px'
                                                    }}
                                                />
                                            </ListItem>
                                        </motion.div>
                                    ))}
                                </List>
                                <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)', my: 1 }} />
                                <Box sx={{ mt: 1, p: 2, display: 'flex', justifyContent: 'center', gap: 1, flexWrap: 'nowrap' }}>
                                    {socialLinks.map(({ href, icon, label }, i) => (
                                        <motion.div
                                            key={label}
                                            initial={{ opacity: 0, y: 10, scale: 0.8 }}
                                            animate={{ opacity: 1, y: 0, scale: 1 }}
                                            transition={{ delay: 0.2 + (i * 0.05) }}
                                        >
                                            <IconButton
                                                component="a"
                                                href={href}
                                                target="_blank"
                                                size="small"
                                                sx={{
                                                    color: 'white',
                                                    bgcolor: 'rgba(255,255,255,0.1)',
                                                    '&:hover': { bgcolor: '#1A9E7F', color: 'white' },
                                                    width: 35, height: 35,
                                                }}
                                            >
                                                {React.cloneElement(icon, { fontSize: "small" })}
                                            </IconButton>
                                        </motion.div>
                                    ))}
                                </Box>
                            </Box>
                        </Drawer>
                    </>
                )}
            </Toolbar>
        </AppBar>
    );
};

const socialLinks = [
    { href: 'https://www.linkedin.com/company/growaitech', icon: <LinkedInIcon />, label: 'LinkedIn' },
    { href: 'https://x.com/GrowAiTech', icon: <TwitterIcon />, label: 'Twitter' },
    { href: 'https://github.com/growaitech', icon: <GitHubIcon />, label: 'GitHub' },
    { href: 'https://www.instagram.com/growaitech', icon: <InstagramIcon />, label: 'Instagram' },
    { href: 'https://www.facebook.com/people/growaitech-company/61568267609033/', icon: <FacebookIcon />, label: 'Facebook' },
];

const socialButtonStyles = {
    color: (theme) => theme.palette.secondary.main,
    '&:hover': {
        color: (theme) => theme.palette.primary.main,
        backgroundColor: 'rgba(0,0,0,0.04)',
    },
    width: '100%',
    justifyContent: 'flex-start',
    padding: '8px 16px',
    borderRadius: '8px'
};

export default Header;
